<template>
  <div>
    <div class="nk-app-root">
      <div class="nk-main">
        <div class="nk-wrap">
          <Header v-show="!loading_page" />
          <div class="nk-content">
            <div class="container wide-xl">
              <div class="nk-content-inner">
                <Navigation v-show="!loading_page" />
                <div class="nk-content-body" v-show="!loading_page">
                  <div class="nk-content-wrap">
                    <div class="nk-block nk-block-lg">
                      <div class="nk-block-head">
                        <div class="nk-block-head-content d-flex justify-content-between align-items-center">
                          <h4 class="nk-block-title">{{ $t('dm_parks_title') }}</h4>
                          <button v-if="canDmEdit" class="btn btn-sm btn-primary" @click="openModal(null)">
                            <em class="icon ni ni-plus"></em> {{ $t('dm_parks_btn_new') }}
                          </button>
                        </div>
                      </div>

                      <div class="form-control-wrap mb-3">
                        <input type="text" class="form-control" :placeholder="$t('dm_common_quick_filter')" v-model="quickFilterText" />
                      </div>

                      <div class="card card-bordered">
                        <div class="dm-table-wrap">
                          <table class="dm-table">
                            <thead>
                              <tr>
                                <th v-for="col in tableColumns" :key="col.field" @click="toggleSort(col.field)" class="dm-table-th">
                                  {{ col.label }}
                                  <span v-if="sortField === col.field" class="dm-sort-icon">{{ sortAsc ? '&#9650;' : '&#9660;' }}</span>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="row in filteredData" :key="row._id" @click="onRowClicked(row)" class="dm-table-row">
                                <td v-for="col in tableColumns" :key="col.field">{{ getCellValue(row, col) }}</td>
                              </tr>
                              <tr v-if="filteredData.length === 0">
                                <td :colspan="tableColumns.length" class="text-center text-muted py-4">{{ $t('dm_common_no_entries_found') }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="modal fade" tabindex="-1" id="modalPark">
                    <div class="modal-dialog" role="document">
                      <div class="modal-content">
                        <a href="#" class="close" data-dismiss="modal" aria-label="Close">
                          <em class="icon ni ni-cross"></em>
                        </a>
                        <div class="modal-header">
                          <h5 class="modal-title">{{ parkEntry._id ? $t('dm_parks_modal_edit') : $t('dm_parks_modal_new') }}</h5>
                        </div>
                        <div class="modal-body">
                          <div class="alert alert-danger" v-if="errorMessage">{{ errorMessage }}</div>
                          <div class="form-group">
                            <label class="form-label">{{ $t('dm_common_col_type') }}</label>
                            <select class="form-control" v-model="parkEntry.typ">
                              <option value="Windpark">{{ $t('dm_parks_type_windpark') }}</option>
                              <option value="Solarpark">{{ $t('dm_parks_type_solarpark') }}</option>
                              <option value="Batteriespeicher">{{ $t('dm_parks_type_battery') }}</option>
                            </select>
                          </div>
                          <div class="form-group">
                            <label class="form-label">{{ $t('dm_common_col_name') }}</label>
                            <input type="text" class="form-control" v-model="parkEntry.name" />
                          </div>
                          <div class="form-group">
                            <label class="form-label">{{ $t('dm_common_col_location') }}</label>
                            <input type="text" class="form-control" v-model="parkEntry.standort_name" />
                          </div>
                          <div class="form-group">
                            <div class="row">
                              <div class="col-6">
                                <label class="form-label">{{ $t('dm_common_col_longitude') }}</label>
                                <input type="number" step="0.000001" class="form-control" v-model.number="parkEntry.einfahrt_geokoordinaten.coordinates[0]" />
                              </div>
                              <div class="col-6">
                                <label class="form-label">{{ $t('dm_common_col_latitude') }}</label>
                                <input type="number" step="0.000001" class="form-control" v-model.number="parkEntry.einfahrt_geokoordinaten.coordinates[1]" />
                              </div>
                            </div>
                          </div>
                          <div class="form-group">
                            <button type="button" class="btn btn-primary" @click="savePark">
                              {{ parkEntry._id ? $t('dm_common_btn_update') : $t('dm_common_btn_create') }}
                            </button>
                          </div>
                        </div>
                        <div class="modal-footer bg-light d-flex justify-content-between">
                          <div>
                            <button v-if="parkEntry._id && canDmManage" type="button" class="btn btn-sm btn-outline-danger" @click="deleteParkAction">
                              <em class="icon ni ni-trash"></em> {{ $t('dm_common_btn_delete') }}
                            </button>
                          </div>
                          <button type="button" class="btn btn-sm btn-secondary" data-dismiss="modal">{{ $t('dm_common_btn_close') }}</button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Footer />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Header from "../../../components/Header.vue";
import Navigation from "../../../components/Navigation.vue";
import Footer from "../../../components/Footer.vue";
import dmTableMixin from "@/mixins/dm-table-mixin";
import dmPermissionsMixin from "@/mixins/dm-permissions-mixin";
import { getParksList, createPark, updatePark, deletePark } from "@/services/dm.service";

function emptyPark() {
  return {
    typ: "Windpark",
    name: "",
    standort_name: "",
    einfahrt_geokoordinaten: { type: "Point", coordinates: [0, 0] },
  };
}

export default {
  name: "DmParks",
  components: { Header, Navigation, Footer },
  mixins: [dmTableMixin, dmPermissionsMixin],
  data() {
    return {
      fullLoader: null,
      parks: [],
      parkEntry: emptyPark(),
      errorMessage: "",
    };
  },
  computed: {
    loading_page() {
      return this.$store.state.loading_page && this.$store.state.loading_page == "setup";
    },
    tableColumns() {
      return [
        { label: this.$t('dm_common_col_type'), field: "typ" },
        { label: this.$t('dm_common_col_name'), field: "name" },
        { label: this.$t('dm_common_col_location'), field: "standort_name" },
        { label: this.$t('dm_common_col_longitude'), field: "lng", valueGetter: function(row) { return row.einfahrt_geokoordinaten && row.einfahrt_geokoordinaten.coordinates ? row.einfahrt_geokoordinaten.coordinates[0] : ""; } },
        { label: this.$t('dm_common_col_latitude'), field: "lat", valueGetter: function(row) { return row.einfahrt_geokoordinaten && row.einfahrt_geokoordinaten.coordinates ? row.einfahrt_geokoordinaten.coordinates[1] : ""; } },
      ];
    },
    filteredData() {
      return this.filterAndSort(this.parks, this.tableColumns);
    },
  },
  created() {
    this.loadParks();
  },
  methods: {
    loadParks() {
      this.showLoader();
      getParksList()
        .then((d) => {
          this.hideLoader();
          if (d.data && d.data.list) {
            this.parks = d.data.list;
            this.$store.commit("set_dm_parks", d.data.list);
          }
        })
        .catch((e) => {
          this.hideLoader();
          this.$store.dispatch("api_error", e);
        });
    },
    onRowClicked(row) {
      if (this.canDmEdit) {
        this.openModal(row);
      }
    },
    openModal(park) {
      this.errorMessage = "";
      var entry = park ? JSON.parse(JSON.stringify(park)) : emptyPark();
      if (!entry.einfahrt_geokoordinaten) entry.einfahrt_geokoordinaten = { type: "Point", coordinates: [0, 0] };
      if (!entry.einfahrt_geokoordinaten.coordinates) entry.einfahrt_geokoordinaten.coordinates = [0, 0];
      this.parkEntry = entry;
      $("#modalPark").modal("show");
    },
    savePark() {
      this.errorMessage = "";
      if (!this.parkEntry.name) {
        this.errorMessage = this.$t('dm_parks_error_name_required');
        return;
      }
      if (this.parkEntry._id) {
        updatePark(this.parkEntry._id, this.parkEntry).then((d) => {
          var err = this.getApiError(d); if (err) { this.errorMessage = err; }
          else { $("#modalPark").modal("hide"); this.loadParks(); }
        });
      } else {
        createPark(this.parkEntry).then((d) => {
          var err = this.getApiError(d); if (err) { this.errorMessage = err; }
          else { $("#modalPark").modal("hide"); this.loadParks(); }
        });
      }
    },
    deleteParkAction() {
      this.$swal({
        title: this.$t('dm_parks_confirm_delete_title'),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: this.$t('dm_common_btn_delete_yes'),
        cancelButtonText: this.$t('dm_common_btn_cancel'),
      }).then((result) => {
        if (result.isConfirmed) {
          deletePark(this.parkEntry._id).then(() => {
            $("#modalPark").modal("hide");
            this.loadParks();
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.dm-table-wrap {
  max-height: calc(100vh - 220px);
  overflow: auto;
}

.dm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 15px;
  table-layout: fixed;
}

.dm-table th,
.dm-table td {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dm-table thead {
  background: #f8f9fa;
  border-bottom: 2px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 1;
}

.dm-table-th {
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #64748b;
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
}

.dm-table-th:hover {
  color: #334155;
}

.dm-sort-icon {
  font-size: 10px;
  margin-left: 4px;
  color: #E8751A;
}

.dm-table tbody td {
  padding: 12px 16px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.dm-table-row {
  cursor: pointer;
  transition: background 0.15s;
}

.dm-table-row:hover {
  background: #f8fafc;
}
</style>
